import React from 'react';
import { Mail, Calendar, ArrowDown } from 'lucide-react';
import { track } from '../utils/analytics';
import { openReservation } from '../utils/reservations';
import DiwaliPoster, { DIWALI_POSTER, DIWALI_DETAILS_FLYER } from '../components/DiwaliPoster';
import { DIWALI_FAQ } from '../seo/config';

export const DIWALI_EMAIL = 'reservations@bricklanela.com';
export const DIWALI_MAILTO = `mailto:${DIWALI_EMAIL}?subject=${encodeURIComponent('Diwali Gala 2026 Reservation')}`;
export const DIWALI_RESY_URL =
  'https://resy.com/cities/los-angeles-ca/venues/bricklane/events/brick-lane-diwali-2026-11-06-7';
export { DIWALI_POSTER };

const GOLD = '#C9A86C';

const EXPERIENCES = [
  {
    num: '01',
    title: 'Live Appetizer Stations',
    body:
      'Elevated artisanal chaats and savory street food delicacies, prepared to order by our chefs as you arrive.',
    image:
      'https://images.unsplash.com/photo-1655740283378-269ebc3dc23a?crop=entropy&cs=srgb&fm=jpg&q=85&w=1400',
    alt: 'Chaat prepared to order at a live station',
  },
  {
    num: '02',
    title: 'Family-Style Grand Feast',
    body:
      'Gather around communal platters and indulge in slow-simmered gravies, aromatic biryanis and artisanal breads from the tandoor.',
    image:
      'https://images.unsplash.com/photo-1563379091339-03b21ab4a4f8?crop=entropy&cs=srgb&fm=jpg&q=85&w=1400',
    alt: 'Biryani served family style',
  },
  {
    num: '03',
    title: 'Curated Open Bar',
    body:
      'Raise a glass with premium spirits, sommelier-selected wines and bespoke Diwali cocktails crafted with vibrant botanicals.',
    image:
      'https://images.unsplash.com/photo-1770164491578-09f08156db2d?crop=entropy&cs=srgb&fm=jpg&q=85&w=1400',
    alt: 'Bartender pouring a craft cocktail',
  },
  {
    num: '04',
    title: 'Live Beats & Henna',
    body:
      'Immerse yourself in high-energy beats from our live DJ, and enjoy complimentary bespoke body art by our master henna artist.',
    image:
      'https://images.unsplash.com/photo-1623217509141-6f735087b50c?crop=entropy&cs=srgb&fm=jpg&q=85&w=1400',
    alt: 'Henna body art',
  },
];

const DETAILS = [
  { label: 'Dates', value: 'Friday, November 6th\nSaturday, November 7th' },
  { label: 'Times', value: 'Doors open 5:30 PM\nDinner served 7:30 PM' },
  { label: 'Venue', value: 'Brick Lane\n1331 East 6th Street, Los Angeles, CA 90021' },
  { label: 'Attire', value: 'Festive Luxe / Desi Chic' },
  { label: 'Seats', value: '$95 per person\nplus tax, gratuity & beverages' },
  { label: 'Reservations', value: `Book on Resy\nor email ${DIWALI_EMAIL}` },
];

// Book Now goes to the Resy event page; email is the direct alternative.
export const ReserveCTA = ({ location, idPrefix, invert = false }) => (
  <div className="flex flex-col sm:flex-row items-stretch sm:items-center gap-4">
    <button
      onClick={() => openReservation(DIWALI_RESY_URL, location)}
      data-testid={`${idPrefix}-book-btn`}
      className="inline-flex items-center justify-center gap-2 px-10 py-4 text-xs md:text-sm uppercase tracking-[0.22em] hover:opacity-85 transition-opacity duration-300"
      style={{ backgroundColor: GOLD, color: '#0a0a0a' }}
    >
      <Calendar size={16} /> Book Now
    </button>
    <a
      href={DIWALI_MAILTO}
      onClick={() => track('Contact', { content_name: 'Diwali Email Reservation', location })}
      data-testid={`${idPrefix}-email-btn`}
      className={`inline-flex items-center justify-center gap-2 px-7 py-4 text-xs md:text-sm tracking-[0.06em] border transition-colors duration-300 break-all text-center ${
        invert
          ? 'text-[#0a0a0a] border-[#0a0a0a]/30 hover:border-[#0a0a0a]'
          : 'text-white border-white/30 hover:border-white'
      }`}
    >
      <Mail size={15} className="flex-shrink-0" /> {DIWALI_EMAIL}
    </a>
  </div>
);

const DiwaliPage = () => {
  const scrollToDetails = () => {
    document.getElementById('diwali-details')?.scrollIntoView({ behavior: 'smooth' });
  };

  return (
    <div className="bg-[#0a0a0a]" data-testid="diwali-page">
      {/* Hero — editorial, left-aligned type + flyer poster */}
      <section className="relative overflow-hidden pt-28 pb-20 md:pt-40 md:pb-28">
        <img
          src="https://images.unsplash.com/photo-1601446052518-f793d82a4a97?crop=entropy&cs=srgb&fm=jpg&q=85&w=1920"
          alt=""
          aria-hidden="true"
          className="absolute inset-0 w-full h-full object-cover opacity-25"
        />
        <div
          className="absolute inset-0"
          style={{ background: 'linear-gradient(90deg, #0a0a0a 10%, rgba(10,10,10,0.72) 60%, rgba(10,10,10,0.9) 100%)' }}
        />
        <div className="relative max-w-7xl mx-auto px-6 grid lg:grid-cols-[1.15fr_0.85fr] gap-14 lg:gap-20 items-center">
          <div>
            <p className="text-[11px] md:text-xs uppercase tracking-[0.4em] mb-6" style={{ color: GOLD }}>
              Brick Lane Presents
            </p>
            <h1
              className="text-white mb-5"
              style={{
                fontFamily: 'var(--font-serif)',
                fontSize: 'clamp(2.75rem, 7vw, 5.25rem)',
                lineHeight: '1.02',
                letterSpacing: '0.01em',
              }}
              data-testid="diwali-hero-title"
            >
              Diwali Gala <span style={{ color: GOLD }}>2026</span>
            </h1>
            <p
              className="italic text-white/80 mb-10 text-lg md:text-2xl"
              style={{ fontFamily: 'var(--font-serif)' }}
            >
              Bites, Beats and Bright Lights
            </p>
            <div className="h-px w-24 mb-10" style={{ backgroundColor: GOLD }} />
            <div className="space-y-2 mb-12">
              <p className="text-white text-base md:text-lg tracking-wide">
                November 6 &amp; 7 &nbsp;·&nbsp; Doors 5:30 PM &nbsp;·&nbsp; Dinner 7:30 PM
              </p>
              <p className="text-white/60 text-sm md:text-base">
                $95 per person, plus tax, gratuity &amp; beverages · Arts District, Los Angeles
              </p>
            </div>
            <ReserveCTA location="diwali-hero" idPrefix="diwali-hero" />
            <button
              onClick={scrollToDetails}
              data-testid="diwali-hero-details-btn"
              className="mt-7 inline-flex items-center gap-2 text-xs uppercase tracking-[0.25em] text-white/60 hover:text-white transition-colors duration-300"
            >
              Event Details <ArrowDown size={14} />
            </button>
          </div>

          <div className="relative">
            <DiwaliPoster testId="diwali-poster" />
          </div>
        </div>
      </section>

      {/* Lead paragraph — crawlable context for "Diwali in Los Angeles" queries */}
      <section className="pt-16 md:pt-24" data-testid="diwali-intro">
        <div className="max-w-3xl mx-auto px-6">
          <p className="text-white/70 text-lg md:text-xl leading-relaxed">
            Brick Lane celebrates <strong className="text-white font-normal">Diwali 2026</strong> with two nights of
            festival of lights dining in the Los Angeles Arts District — Friday, November 6 and Saturday, November 7.
            Expect live chaat stations, a family-style grand feast from the woodfire and tandoor, a curated open bar of
            bespoke Diwali cocktails, a live DJ and complimentary henna. $95 per person, plus tax, gratuity and
            beverages.
          </p>
        </div>
      </section>

      {/* Experiences — alternating editorial rows */}
      <section className="py-4 md:py-10" data-testid="diwali-experiences">
        {EXPERIENCES.map((exp, idx) => (
          <div
            key={exp.num}
            className="max-w-7xl mx-auto px-6 py-14 md:py-20 border-t"
            style={{ borderColor: 'rgba(201,168,108,0.25)' }}
          >
            <div className="grid md:grid-cols-2 gap-10 md:gap-16 items-center">
              <div className={idx % 2 === 1 ? 'md:order-2' : ''}>
                <img
                  src={exp.image}
                  alt={exp.alt}
                  loading="lazy"
                  className="w-full aspect-[4/3] object-cover"
                  data-testid={`diwali-experience-image-${idx}`}
                />
              </div>
              <div className={idx % 2 === 1 ? 'md:order-1' : ''}>
                <p className="text-xs tracking-[0.35em] mb-5" style={{ color: GOLD }}>
                  {exp.num} —
                </p>
                <h2
                  className="text-white mb-5"
                  style={{
                    fontFamily: 'var(--font-serif)',
                    fontSize: 'clamp(1.75rem, 3.5vw, 2.75rem)',
                    lineHeight: '1.15',
                  }}
                >
                  {exp.title}
                </h2>
                <p className="text-white/65 text-base md:text-lg leading-relaxed max-w-xl">{exp.body}</p>
              </div>
            </div>
          </div>
        ))}
      </section>

      {/* Event details */}
      <section id="diwali-details" className="bg-[#F7F3EC] py-20 md:py-28" data-testid="diwali-details">
        <div className="max-w-6xl mx-auto px-6">
          <p className="text-[11px] uppercase tracking-[0.4em] mb-4" style={{ color: '#9A7E45' }}>
            Event Details
          </p>
          <h2
            className="mb-14 text-[#0a0a0a]"
            style={{ fontFamily: 'var(--font-serif)', fontSize: 'clamp(2rem, 4.5vw, 3.25rem)', lineHeight: '1.1' }}
          >
            Two nights of light.
          </h2>
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-y-12 gap-x-12">
            {DETAILS.map((d) => (
              <div key={d.label} className="border-t border-[#0a0a0a]/15 pt-5">
                <p className="text-[11px] uppercase tracking-[0.25em] mb-3" style={{ color: '#9A7E45' }}>
                  {d.label}
                </p>
                <p className="text-[#1a1a1a] text-base md:text-lg leading-relaxed whitespace-pre-line">
                  {d.value}
                </p>
              </div>
            ))}
          </div>
          <div className="mt-16 grid lg:grid-cols-[1fr_0.75fr] gap-12 items-start">
            <div>
              <ReserveCTA location="diwali-details" idPrefix="diwali-details" invert />
            </div>
            <DiwaliPoster
              src={DIWALI_DETAILS_FLYER}
              alt="Brick Lane Diwali Gala 2026 invitation — live appetizer stations, family-style grand feast, curated open bar, live beats and henna, with full event details"
              testId="diwali-details-flyer"
            />
          </div>
        </div>
      </section>

      {/* FAQ — answers mirror the FAQPage JSON-LD so Google/AI assistants can quote them */}
      <section className="bg-[#0a0a0a] py-20 md:py-28 border-t" style={{ borderColor: 'rgba(201,168,108,0.25)' }} data-testid="diwali-faq">
        <div className="max-w-5xl mx-auto px-6">
          <p className="text-[11px] uppercase tracking-[0.4em] mb-4" style={{ color: GOLD }}>
            Good to know
          </p>
          <h2
            className="text-white mb-14"
            style={{ fontFamily: 'var(--font-serif)', fontSize: 'clamp(1.9rem, 4vw, 3rem)', lineHeight: '1.1' }}
          >
            Diwali in Los Angeles, answered.
          </h2>
          <div className="grid md:grid-cols-2 gap-x-14 gap-y-10">
            {DIWALI_FAQ.map((item, idx) => (
              <div key={item.q} data-testid={`diwali-faq-item-${idx}`}>
                <h3 className="text-white text-base md:text-lg font-medium mb-3">{item.q}</h3>
                <p className="text-white/60 text-sm md:text-base leading-relaxed">{item.a}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Closing CTA */}
      <section className="relative py-24 md:py-32 overflow-hidden" data-testid="diwali-closing-cta">
        <img
          src="https://images.unsplash.com/photo-1706168586091-0a412cd6a830?crop=entropy&cs=srgb&fm=jpg&q=85&w=1920"
          alt=""
          aria-hidden="true"
          loading="lazy"
          className="absolute inset-0 w-full h-full object-cover opacity-30"
        />
        <div className="absolute inset-0 bg-[#0a0a0a]/70" />
        <div className="relative max-w-3xl mx-auto px-6 text-center">
          <h2
            className="text-white mb-6"
            style={{ fontFamily: 'var(--font-serif)', fontSize: 'clamp(2rem, 5vw, 3.5rem)', lineHeight: '1.1' }}
          >
            Celebrate with us
          </h2>
          <p className="text-white/70 text-base md:text-lg mb-10">
            Seats are limited for both evenings. Book on Resy, or email {DIWALI_EMAIL} to reserve your table.
          </p>
          <div className="flex justify-center">
            <ReserveCTA location="diwali-footer" idPrefix="diwali-footer" />
          </div>
        </div>
      </section>
    </div>
  );
};

export default DiwaliPage;
